/**
 * QuickTracking Web SDK (aplus_queue) 埋点封装
 * 参考：《媒体客户端生产传播效果数据埋点技术要求》H5 接入章节
 */

const isAplusReady = () =>
  typeof window !== "undefined" && Array.isArray(window.aplus_queue);

/**
 * 安全推送 aplus 命令
 */
function push(action, args) {
  if (!isAplusReady()) {
    if (process.env.NODE_ENV === "development") {
      console.warn("[qt] aplus_queue not ready, skip", action, args);
    }
    return;
  }
  window.aplus_queue.push({ action, arguments: args });
}

/**
 * 全局属性：用户身份相关
 * _user_id / registration_source / gender / age / first_account_regist_date
 */
export function setGlobalUserProfile(user = {}) {
  const {
    id,
    registrationSource,
    gender,
    age,
    firstAccountRegistDate,
  } = user;

  if (id != null) {
    push("aplus.setMetaInfo", ["_user_id", String(id)]);
  }
  if (registrationSource != null) {
    push("aplus.setMetaInfo", ["registration_source", String(registrationSource)]);
  }
  if (gender != null) {
    push("aplus.setMetaInfo", ["gender", String(gender)]);
  }
  if (age != null) {
    push("aplus.setMetaInfo", ["age", String(age)]);
  }
  if (firstAccountRegistDate != null) {
    push("aplus.setMetaInfo", ["first_account_regist_date", String(firstAccountRegistDate)]);
  }
}

/**
 * 过滤对象：剔除 null/undefined，值统一转为字符串
 */
function normalizeProps(props = {}) {
  const result = {};
  Object.keys(props).forEach((key) => {
    const val = props[key];
    if (val === null || val === undefined) {
      result[key] = "";
    } else {
      result[key] = String(val);
    }
  });
  return result;
}

/**
 * 页面浏览 PV 上报
 * @param {string} eventCode  事件编码，如 article_details_pg
 * @param {object} props      事件属性
 */
export function sendPV(eventCode, props = {}) {
  if (!eventCode) return;

  const data = normalizeProps({
    event_type: "pageview",
    event_code: eventCode,
    ...props,
  });
  console.log("[QT] SDK not ready, delay sendPV:", eventCode, props);
  push("aplus.sendPV", [{ is_auto: false }, data]);
}

/* ===================== 稿件详情页 PV 快捷方法 ===================== */

const CONTENT_TYPE_MAP = {
  5: "新闻稿件",
  6: "新闻稿件",
  7: "其他",
  8: "其他",
  9: "新闻稿件",
  11: "新闻稿件",
  12: "其他",
  101: "其他",
};

/**
 * 将日期字符串或秒/毫秒时间戳转换为毫秒时间戳字符串
 * @param {string|number} dateVal
 * @returns {string}
 */
function toTimestamp(dateVal) {
  if (!dateVal) return null;
  var ts;
  if (typeof dateVal === 'number') {
    // 已经是毫秒或秒时间戳，统一转成毫秒级13位
    ts = dateVal > 9999999999 ? dateVal : dateVal * 1000;
  } else {
    ts = new Date(dateVal).getTime();
    if (isNaN(ts)) return null;
  }
  return ts;
}

/**
 * 上报页面浏览事件（PV）
 * @param {string} eventCode 事件编码
 * @param {Object} props 事件属性
 */
export function qtSendPV(eventCode, props = {}) {
  if (typeof window !== "undefined" && typeof window.__qtDebugLog === "function") {
    window.__qtDebugLog("sendPV start: " + eventCode, "info");
  }
  // QT sendPV 的事件编码/页面编码通过 page_name 传递，而不是 props 里的任意字段
  const pvProps = Object.assign({}, props, { page_name: eventCode });
  if (!isReady()) {
    console.warn("[QT] SDK not ready, delay sendPV:", eventCode, props);
    if (typeof window !== "undefined" && typeof window.__qtDebugLog === "function") {
      window.__qtDebugLog("sendPV delay: SDK not ready", "warn");
    }
    setTimeout(() => {
      if (isReady()) {
        window.aplus_queue.push({
          action: "aplus.sendPV",
          arguments: [{ is_auto: false }, pvProps],
        });
        console.log("[QT] sendPV success(delay):", eventCode, pvProps);
        if (typeof window !== "undefined" && typeof window.__qtDebugLog === "function") {
          window.__qtDebugLog("sendPV success(delay): " + eventCode, "success");
        }
      } else {
        console.warn("[QT] sendPV dropped, SDK still not ready:", eventCode, props);
        if (typeof window !== "undefined" && typeof window.__qtDebugLog === "function") {
          window.__qtDebugLog("sendPV dropped: " + eventCode, "error");
        }
      }
    }, 500);
    return;
  }
  window.aplus_queue.push({
    action: "aplus.sendPV",
    arguments: [{ is_auto: false }, pvProps],
  });
  console.log("[QT] sendPV success:", eventCode, pvProps);
  if (typeof window !== "undefined" && typeof window.__qtDebugLog === "function") {
    window.__qtDebugLog("sendPV success: " + eventCode, "success");
  }
}

/**
 * 稿件详情页浏览埋点
 * @param {Object} article  稿件详情对象
 * @param {Object} extra    页面上下文信息，如 specialName
 */
export function trackArticleDetailPg(article = {}, extra = {}) {
  const isOriginal =
    article.isOriginal == 1 || article.isOriginal == "是" ? "是" : "否";

  sendPV("article_details_pg", {
    current_page_name: "稿件详情页",
    current_page_id: "article_details_pg",
    article_name: article.title || "",
    article_type:
      CONTENT_TYPE_MAP[article.contentType] || "其他",
    article_author: article.reporter || "无",
    article_id: String(article.contentId || article.id || ""),
    originality: isOriginal,
    current_create_date: toTimestamp(article.publishTime || article.creationTime),
    special_topic_name: extra.specialName || article.specialName || "",
  });
}

export default {
  setGlobalUserProfile,
  sendPV,
  qtSendPV,
  trackArticleDetailPg,
};
